import { arr, bool, int, num, obj, s, strings, type Json, type JsonObject } from "./json";

// 所有解码都是宽松的：字段缺失或类型不对就当没有，服务端加字段/改类型不会让整个结果失败（和 iOS 一致）。

/** session.history / resume / create 返回的一条展示用消息 */
export interface TranscriptMessage {
  /** user / assistant / system / tool */
  role: string;
  text?: string;
  timestamp?: number;
  row_id?: number;
  display_kind?: string;
  /** tool 行：工具名 */
  name?: string;
  /** tool 行：参数预览 */
  context?: string;
  args?: Json;
  reasoning?: string;
}

export function parseTranscriptMessage(raw: unknown): TranscriptMessage | undefined {
  const o = obj(raw);
  if (!o) return undefined;
  return {
    role: s(o.role) ?? "system",
    text: s(o.text),
    timestamp: num(o.timestamp),
    row_id: int(o.row_id),
    display_kind: s(o.display_kind),
    name: s(o.name),
    context: s(o.context),
    args: o.args === undefined ? undefined : o.args,
    reasoning: s(o.reasoning),
  };
}

export function parseMessages(raw: unknown): TranscriptMessage[] {
  return (arr(raw) ?? []).map(parseTranscriptMessage).filter((m): m is TranscriptMessage => !!m);
}

/** Token / 上下文 / 费用计数 */
export interface UsageStats {
  model?: string;
  input?: number;
  output?: number;
  reasoning?: number;
  total?: number;
  calls?: number;
  context_used?: number;
  context_max?: number;
  context_percent?: number;
  cost_usd?: number;
  avg_tps?: number;
}

export function parseUsage(raw: unknown): UsageStats | undefined {
  const o = obj(raw);
  if (!o) return undefined;
  return {
    model: s(o.model), input: int(o.input), output: int(o.output), reasoning: int(o.reasoning), total: int(o.total),
    calls: int(o.calls), context_used: int(o.context_used), context_max: int(o.context_max),
    context_percent: int(o.context_percent), cost_usd: num(o.cost_usd), avg_tps: num(o.avg_tps),
  };
}

/** 实时会话设置快照（session.info 事件 / create / resume 的 info） */
export interface SessionInfo {
  model?: string;
  provider?: string;
  reasoning_effort?: string;
  fast?: boolean;
  yolo?: boolean;
  approval_mode?: string;
  cwd?: string;
  personality?: string;
  running?: boolean;
  turn_started_at?: number;
  title?: string;
  stored_session_id?: string;
  version?: string;
  release_date?: string;
  profile_name?: string;
  usage?: UsageStats;
}

export function parseSessionInfo(raw: unknown): SessionInfo | undefined {
  const o = obj(raw);
  if (!o) return undefined;
  return {
    model: s(o.model), provider: s(o.provider), reasoning_effort: s(o.reasoning_effort), fast: bool(o.fast),
    yolo: bool(o.yolo), approval_mode: s(o.approval_mode), cwd: s(o.cwd), personality: s(o.personality),
    running: bool(o.running), turn_started_at: num(o.turn_started_at), title: s(o.title),
    stored_session_id: s(o.stored_session_id), version: s(o.version), release_date: s(o.release_date),
    profile_name: s(o.profile_name), usage: parseUsage(o.usage),
  };
}

/** 事件里的部分快照合并进当前快照（只覆盖有值的字段） */
export function mergeInfo(base: SessionInfo, other: SessionInfo): SessionInfo {
  const out: SessionInfo = { ...base };
  for (const [k, v] of Object.entries(other)) if (v !== undefined) (out as Record<string, unknown>)[k] = v;
  return out;
}

/** session.create / resume / activate / branch 的结果 */
export interface SessionSnapshot {
  session_id: string;
  stored_session_id?: string;
  message_count?: number;
  messages: TranscriptMessage[];
  info?: SessionInfo;
  title?: string;
}

export function parseSnapshot(raw: unknown, method: string): SessionSnapshot {
  const o = obj(raw);
  const sid = s(o?.session_id);
  if (!o || sid === undefined) throw new Error(`${method}：缺少 session_id`);
  return {
    session_id: sid,
    stored_session_id: s(o.stored_session_id),
    message_count: int(o.message_count),
    messages: parseMessages(o.messages),
    info: parseSessionInfo(o.info),
    title: s(o.title),
  };
}

/** 存储 id：优先顶层字段，其次 info 里的 */
export function resolvedStoredID(snap: SessionSnapshot): string | undefined {
  return snap.stored_session_id ? snap.stored_session_id : snap.info?.stored_session_id;
}

/** session.list 的一行（存储会话） */
export interface SessionSummary {
  id: string;
  resolved_id?: string;
  title?: string;
  preview?: string;
  started_at?: number;
  message_count?: number;
  source?: string;
}

export function parseSessionSummary(raw: unknown): SessionSummary | undefined {
  const o = obj(raw);
  const id = s(o?.id);
  if (!o || !id) return undefined;
  return {
    id, resolved_id: s(o.resolved_id), title: s(o.title), preview: s(o.preview),
    started_at: num(o.started_at), message_count: int(o.message_count), source: s(o.source),
  };
}

export function sessionDisplayTitle(x: SessionSummary): string {
  const t = x.title?.trim();
  if (t) return t;
  const p = x.preview?.trim();
  if (p) return [...p].slice(0, 40).join("");
  return "新对话";
}

// MARK: Bot（profiles）

export interface ProfileSummary {
  name: string;
  path?: string;
  is_default: boolean;
  model?: string;
  provider?: string;
  description?: string;
  display_name?: string;
  skill_count?: number;
  has_avatar: boolean;
  last_session?: { id: string; title?: string; preview?: string };
  /** Bot Mode 的显示名（profile.yaml 里 ui_meta["hermes-bots"].title） */
  botTitle?: string;
  /** ui_meta["hermes-bots"] 原样保留，改名时只改 title */
  botMeta?: JsonObject;
}

export function parseProfile(raw: unknown): ProfileSummary | undefined {
  const o = obj(raw);
  const name = s(o?.name);
  if (!o || !name) return undefined;
  const meta = obj(obj(o.ui_meta)?.["hermes-bots"]);
  const title = s(meta?.title)?.trim();
  const last = obj(o.last_session);
  return {
    name, path: s(o.path), is_default: bool(o.is_default) ?? false, model: s(o.model), provider: s(o.provider),
    description: s(o.description), display_name: s(o.display_name), skill_count: int(o.skill_count),
    has_avatar: bool(o.has_avatar) ?? false,
    last_session: last && s(last.id) ? { id: s(last.id)!, title: s(last.title), preview: s(last.preview) } : undefined,
    botTitle: title ? title : undefined,
    botMeta: meta,
  };
}

export function profileTitle(p: ProfileSummary): string {
  if (p.botTitle) return p.botTitle;
  if (p.display_name) return p.display_name;
  return p.is_default ? "Hermes" : p.name;
}

export interface ProfileDetail {
  name: string;
  description?: string;
  soul?: string;
  model?: { provider?: string; default?: string };
  skills?: { name: string; enabled?: boolean; label?: string; description?: string }[];
  toolsets?: { name: string; enabled?: boolean; label?: string; description?: string }[];
}

export function parseProfileDetail(raw: unknown): ProfileDetail {
  const o = obj(raw) ?? {};
  const toggles = (v: unknown) => (arr(v) ?? []).map(obj).filter(Boolean).map((t) => ({
    name: s(t!.name) ?? "", enabled: bool(t!.enabled), label: s(t!.label), description: s(t!.description),
  }));
  const m = obj(o.model);
  return {
    name: s(o.name) ?? "", description: s(o.description), soul: s(o.soul),
    model: m ? { provider: s(m.provider), default: s(m.default) } : undefined,
    skills: toggles(o.skills), toolsets: toggles(o.toolsets),
  };
}

// MARK: 定时任务

export interface CronJob {
  job_id: string;
  name?: string;
  skill?: string;
  prompt_preview?: string;
  schedule?: string;
  deliver?: string;
  next_run_at?: string;
  last_run_at?: string;
  last_status?: string;
  last_error?: string;
  last_delivery_error?: string;
  enabled: boolean;
  state?: string;
  paused_reason?: string;
}

export function parseCronJob(raw: unknown): CronJob | undefined {
  const o = obj(raw);
  const id = s(o?.job_id);
  if (!o || !id) return undefined;
  return {
    job_id: id, name: s(o.name), skill: s(o.skill), prompt_preview: s(o.prompt_preview), schedule: s(o.schedule),
    deliver: s(o.deliver), next_run_at: s(o.next_run_at), last_run_at: s(o.last_run_at), last_status: s(o.last_status),
    last_error: s(o.last_error), last_delivery_error: s(o.last_delivery_error), enabled: bool(o.enabled) ?? true,
    state: s(o.state), paused_reason: s(o.paused_reason),
  };
}

export function cronDisplayName(j: CronJob): string {
  if (j.name) return j.name;
  if (j.prompt_preview) return [...j.prompt_preview].slice(0, 30).join("");
  return j.job_id;
}

export function cronIsPaused(j: CronJob): boolean {
  return !j.enabled || j.state === "paused";
}

export interface CronManageResult {
  success?: boolean;
  error?: string;
  jobs?: CronJob[];
  gateway_running?: boolean;
  warning?: string;
}

export function parseCronManage(raw: unknown): CronManageResult {
  const o = obj(raw) ?? {};
  const jobs = arr(o.jobs);
  return {
    success: bool(o.success), error: s(o.error), warning: s(o.warning), gateway_running: bool(o.gateway_running),
    jobs: jobs ? jobs.map(parseCronJob).filter((j): j is CronJob => !!j) : undefined,
  };
}

/** 定时任务的一次运行：本身就是一个存储会话，可以直接 resume 查看 */
export interface CronRun {
  id: string;
  title?: string;
  started_at?: number;
  ended_at?: number;
  message_count?: number;
  tool_call_count?: number;
  is_active?: boolean;
}

export function parseCronRun(raw: unknown): CronRun | undefined {
  const o = obj(raw);
  const id = s(o?.id);
  if (!o || !id) return undefined;
  return {
    id, title: s(o.title), started_at: num(o.started_at), ended_at: num(o.ended_at), message_count: int(o.message_count),
    tool_call_count: int(o.tool_call_count), is_active: bool(o.is_active),
  };
}

export interface CronDeliveryTarget { id: string; name: string; home_target_set?: boolean }

// MARK: 模型 / 命令

export interface ModelProvider {
  slug: string;
  name: string;
  models?: string[];
  is_current?: boolean;
  authenticated?: boolean;
  featured_models?: string[];
  auth_type?: string;
  key_env?: string;
  is_user_defined?: boolean;
  aliases?: string[];
}

export function providerMatches(p: ModelProvider, provider: string | undefined): boolean {
  if (!provider) return false;
  return provider === p.slug || (p.aliases ?? []).includes(provider) || provider === `custom:${p.slug}`;
}

export interface ModelOptionsResult {
  providers: ModelProvider[];
  model?: string;
  provider?: string;
}

export function parseModelOptions(raw: unknown): ModelOptionsResult {
  const o = obj(raw) ?? {};
  const providers = (arr(o.providers) ?? []).map(obj).filter(Boolean).map((p) => ({
    slug: s(p!.slug) ?? "", name: s(p!.name) ?? s(p!.slug) ?? "", models: strings(p!.models),
    is_current: bool(p!.is_current), authenticated: bool(p!.authenticated), featured_models: strings(p!.featured_models),
    auth_type: s(p!.auth_type), key_env: s(p!.key_env), is_user_defined: bool(p!.is_user_defined), aliases: strings(p!.aliases),
  })).filter((p) => p.slug);
  return { providers, model: s(o.model), provider: s(o.provider) };
}

export interface ConfigSetResult {
  key?: string;
  warning?: string;
  confirm_required?: boolean;
  confirm_message?: string;
  info?: SessionInfo;
}

export function parseConfigSet(raw: unknown): ConfigSetResult {
  const o = obj(raw) ?? {};
  return {
    key: s(o.key), warning: s(o.warning), confirm_required: bool(o.confirm_required),
    confirm_message: s(o.confirm_message), info: parseSessionInfo(o.info),
  };
}

export interface SlashCommand { name: string; summary: string; category: string }

export function parseCommandsCatalog(raw: unknown): SlashCommand[] {
  const o = obj(raw) ?? {};
  const seen = new Set<string>();
  const out: SlashCommand[] = [];
  const source: [Json, string][] = [];
  for (const c of arr(o.categories) ?? []) {
    const cat = obj(c);
    for (const pair of arr(cat?.pairs) ?? []) source.push([pair, s(cat?.name) ?? ""]);
  }
  for (const pair of arr(o.pairs) ?? []) source.push([pair, ""]);
  for (const [pair, category] of source) {
    const p = strings(pair) ?? [];
    const raw0 = p[0];
    if (!raw0) continue;
    const name = raw0.startsWith("/") ? raw0 : "/" + raw0;
    if (seen.has(name)) continue;
    seen.add(name);
    out.push({ name, summary: p[1] ?? "", category });
  }
  return out;
}

export interface SlashExecResult {
  output?: string;
  warning?: string;
  /** exec / alias / plugin / send / skill / prefill */
  type?: string;
  message?: string;
  notice?: string;
  target?: string;
  display?: string;
  status?: string;
}

export function parseSlashExec(raw: unknown): SlashExecResult {
  const o = obj(raw) ?? {};
  return {
    output: s(o.output), warning: s(o.warning), type: s(o.type), message: s(o.message), notice: s(o.notice),
    target: s(o.target), display: s(o.display), status: s(o.status),
  };
}

export interface ImageAttachResult { attached: boolean; name?: string; path?: string; message?: string }

export interface FileAttachResult {
  /** 写进消息里的引用（`@file:相对路径`） */
  refText: string;
  path?: string;
  name: string;
}
